import { useSyncExternalStore } from 'react';

/** Where the assistant just worked: the doc, and in it the blocks or canvas elements. */
export interface AssistantFollowPlace {
  docId: string;
  blockIds: readonly string[];
  elementIds: readonly string[];
  /** Tells two visits of the same place apart (the tool call that reached it). */
  key: string;
}

export interface AssistantFollowSnapshot {
  /** The chat session whose turn acts in the app and invites the viewer to follow. */
  readonly offered: string | null;
  /** The chat session whose assistant the view follows. */
  readonly followed: string | null;
  /** The place the followed assistant worked last; the view goes there. */
  readonly place: AssistantFollowPlace | null;
}

/** One step of a turn as the follow sees it: whether it acted in the app, and where. */
export interface AssistantFollowStep {
  acted: boolean;
  place: AssistantFollowPlace | null;
}

/** The tools that act in the app itself without changing a doc. */
const APP_ACTION_TOOLS: ReadonlySet<string> = new Set([
  'workspace_operate',
  'workspace_navigate',
]);

interface ToolResultLike {
  type: string;
  toolName?: string;
  toolCallId?: string;
  result?: unknown;
  /** The tool only read: its refs name where it looked. */
  readOnly?: boolean;
  affectedRefs?: readonly {
    docId?: string;
    blockId?: string;
    elementId?: string;
  }[];
}

/**
 * Reads a stream object of a turn: a tool result that changed a doc names its place (the refs
 * of its first doc); operating or navigating the app acts without a place of its own.
 */
export function assistantFollowStepOf(
  object: ToolResultLike
): AssistantFollowStep | null {
  if (object.type !== 'tool-result' || !object.toolName || object.readOnly)
    return null;
  const result = object.result as { success?: unknown } | undefined;
  if (result?.success === false) return null;
  const docId = object.affectedRefs?.find(ref => ref.docId)?.docId;
  if (docId) {
    const refs = object.affectedRefs!.filter(ref => ref.docId === docId);
    return {
      acted: true,
      place: {
        docId,
        blockIds: refs.flatMap(ref => (ref.blockId ? [ref.blockId] : [])),
        elementIds: refs.flatMap(ref => (ref.elementId ? [ref.elementId] : [])),
        key: object.toolCallId ?? `${object.toolName}:${docId}`,
      },
    };
  }
  return APP_ACTION_TOOLS.has(object.toolName)
    ? { acted: true, place: null }
    : null;
}

type Listener = () => void;

/**
 * Following the assistant in a workspace, like following a moderator: the turn that acts in the
 * app offers it, following takes the view to every place the assistant works, and it ends with
 * the turn, by stopping or by the viewer's own hand.
 */
export class AssistantFollowStore {
  private snapshot: AssistantFollowSnapshot = {
    offered: null,
    followed: null,
    place: null,
  };
  private readonly listeners = new Set<Listener>();
  /** The last place of each turn that runs, so following later starts there. */
  private readonly lastPlace = new Map<string, AssistantFollowPlace>();
  /** Turns whose offer the viewer closed: they do not offer again. */
  private readonly dismissed = new Set<string>();
  /** Turns this window sent: their steps reach it from its own stream. */
  private readonly driven = new Set<string>();

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AssistantFollowSnapshot => this.snapshot;

  /** This window sent the turn of the chat session and reports its steps itself. */
  drive(sessionId: string): void {
    this.driven.add(sessionId);
  }

  /** Whether this window reports the steps of the session's turn from its own stream. */
  drives(sessionId: string): boolean {
    return this.driven.has(sessionId);
  }

  /** A step of a running turn of this chat session. */
  report(sessionId: string, step: AssistantFollowStep): void {
    if (!step.acted) return;
    if (step.place) this.lastPlace.set(sessionId, step.place);
    const { offered, followed } = this.snapshot;
    if (followed === sessionId) {
      if (step.place) this.publish({ ...this.snapshot, place: step.place });
      return;
    }
    if (followed || offered === sessionId || this.dismissed.has(sessionId))
      return;
    this.publish({ ...this.snapshot, offered: sessionId });
  }

  follow(sessionId: string): void {
    if (this.snapshot.followed === sessionId) return;
    this.publish({
      offered: null,
      followed: sessionId,
      place: this.lastPlace.get(sessionId) ?? null,
    });
  }

  /** The viewer closed the offer of the running turn. */
  dismiss(): void {
    const { offered } = this.snapshot;
    if (!offered) return;
    this.dismissed.add(offered);
    this.publish({ ...this.snapshot, offered: null });
  }

  detach(): void {
    if (!this.snapshot.followed) return;
    this.publish({ ...this.snapshot, followed: null, place: null });
  }

  /** The turn of this chat session ended: its offer and the following end with it. */
  endTurn(sessionId: string): void {
    this.lastPlace.delete(sessionId);
    this.driven.delete(sessionId);
    this.dismissed.delete(sessionId);
    const { offered, followed } = this.snapshot;
    if (offered !== sessionId && followed !== sessionId) return;
    this.publish({
      offered: offered === sessionId ? null : offered,
      followed: followed === sessionId ? null : followed,
      place: followed === sessionId ? null : this.snapshot.place,
    });
  }

  private publish(snapshot: AssistantFollowSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener();
  }
}

const stores = new Map<string, AssistantFollowStore>();

export function getAssistantFollowStore(
  workspaceId: string
): AssistantFollowStore {
  let store = stores.get(workspaceId);
  if (!store) {
    store = new AssistantFollowStore();
    stores.set(workspaceId, store);
  }
  return store;
}

export function useAssistantFollow(
  store: AssistantFollowStore
): AssistantFollowSnapshot {
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
}
